import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _reduce from "lodash/reduce";
import _fromPairs from "lodash/fromPairs";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
const MIN_CONTENT_HEIGHT = 100;
const getBottomPadding = _ref => {
  let {
    containerHeight,
    headersHeight,
    items
  } = _ref;
  if (items.length === 0) {
    return 0;
  }
  const lastContentHeight = items[items.length - 1].contentHeight;
  const padding = containerHeight - headersHeight - lastContentHeight;
  return Math.max(0, padding);
};
const getStackUpShiftings = _ref2 => {
  let {
    items,
    scrollTop
  } = _ref2;
  const shiftings = [];
  let stackedContentsHeight = 0;
  let itemIndex = 0;
  let shouldStack = true;
  while (shouldStack) {
    const item = items[itemIndex];
    const shifting = scrollTop - stackedContentsHeight;
    if (shifting > 0) {
      shiftings.push(shifting);
      shouldStack = itemIndex !== items.length - 1;
    } else {
      shouldStack = false;
    }
    if (shouldStack) {
      stackedContentsHeight += item.contentHeight;
      itemIndex += 1;
    }
  }
  return shiftings;
};
const getStackDownShiftings = _ref3 => {
  let {
    containerHeight,
    items,
    scrollTop
  } = _ref3;
  const shiftings = [];
  let stackedHeadersHeight = 0;
  const totalContentHeight = _reduce(items, (sum, _ref4) => {
    let {
      contentHeight,
      headerHeight
    } = _ref4;
    return sum + headerHeight + contentHeight;
  }, 0);
  let distanceToBottomContent = 0;
  let itemIndex = items.length - 1;
  let shouldStack = true;
  while (shouldStack) {
    const item = items[itemIndex];
    const shifting = -item.contentHeight - distanceToBottomContent + totalContentHeight - scrollTop - containerHeight + stackedHeadersHeight;
    if (shifting > 0) {
      shiftings.unshift(-shifting);
      shouldStack = itemIndex !== 0;
    } else {
      shouldStack = false;
    }
    if (shouldStack) {
      distanceToBottomContent += item.headerHeight + item.contentHeight;
      stackedHeadersHeight += item.headerHeight;
      itemIndex -= 1;
    }
  }
  return shiftings;
};

/**
 * This method computes:
 * - The bottom padding to add to the container (if necessary) in order to have
 *   all titles stacked when the user scrolls to the end of the component.
 *   Value is accessible under `bottomPadding` property.
 * - Distance to shift titles to make them sticky. Value is accessible under
 *   `headerShiftings` property keyed by item key.
 * - scrolling distance to apply to the container to focus on a specific item.
 *   Value accessible under `scrollToHeaders` property keyed by item key.
 *
 * @param {Object} view - Structure to describe the current view state
 * @param {number} view.containerHeight - Height in px of the container
 * @param {number} view.scrollTop - Distance in px from top content to the top container
 * @param {Object[]} view.items - Structure to describe a view item
 * @param {string} view.items[].key - View item identifier
 * @param {number} view.items[].headerHeight - View item header height in px
 * @param {number} view.items[].contentHeight - View item content height in px
 * @returns {Object} - Style to apply to the component to make component sticky
 */
const computeStickyState = _ref5 => {
  let {
    containerHeight,
    items,
    scrollTop
  } = _ref5;
  if (items.length === 0) {
    return {
      bottomPadding: 0,
      headerShiftings: {},
      scrollToHeaders: {}
    };
  }
  const headerShiftings = _fromPairs(items.map(_ref6 => {
    let {
      key
    } = _ref6;
    return [key, 0];
  }));
  const headersHeight = _reduce(items, (height, _ref7) => {
    let {
      headerHeight
    } = _ref7;
    return height + headerHeight;
  }, 0);

  // Under this condition, better to deactivate the feature (screen too small)
  if (containerHeight - headersHeight < MIN_CONTENT_HEIGHT) {
    return {
      bottomPadding: 0,
      headerShiftings,
      scrollToHeaders: _reduce(items, (_ref8, _ref9) => {
        let {
          previousHeight,
          scrollToHeaders
        } = _ref8;
        let {
          contentHeight,
          headerHeight,
          key
        } = _ref9;
        return {
          previousHeight: previousHeight + headerHeight + contentHeight,
          scrollToHeaders: _objectSpread(_objectSpread({}, scrollToHeaders), {}, {
            [key]: previousHeight
          })
        };
      }, {
        previousHeight: 0,
        scrollToHeaders: {}
      }).scrollToHeaders
    };
  }
  const stackUpShiftings = getStackUpShiftings({
    items,
    scrollTop
  });
  const stackDownShiftings = getStackDownShiftings({
    containerHeight,
    items,
    scrollTop
  });
  const bottomPadding = getBottomPadding({
    containerHeight,
    headersHeight,
    items
  });
  return {
    bottomPadding,
    headerShiftings: _objectSpread(_objectSpread(_objectSpread({}, headerShiftings), _fromPairs(stackUpShiftings.map((value, index) => [items[index].key, value]))), _fromPairs(stackDownShiftings.reverse().map((value, index) => [items[items.length - index - 1].key, value]))),
    scrollToHeaders: _reduce(items, (_ref10, _ref11) => {
      let {
        previousHeight,
        scrollToHeaders
      } = _ref10;
      let {
        contentHeight,
        key
      } = _ref11;
      return {
        previousHeight: previousHeight + contentHeight,
        scrollToHeaders: _objectSpread(_objectSpread({}, scrollToHeaders), {}, {
          [key]: previousHeight
        })
      };
    }, {
      previousHeight: 0,
      scrollToHeaders: {}
    }).scrollToHeaders
  };
};
export default computeStickyState;